/**
 * ライブ中の軽量ヘルス監視（CPU 監視なし）。
 * 推論遅延・連続失敗・映像ストールを検知し、HUD へ通知する。
 */
import { devConfig } from "./appConfig.js";
import { uiLexicon } from "./uiLexicon.js";

/** @typedef {'inferSlow' | 'inferFail' | 'videoStall'} HealthCode */

/** @typedef {{ kind: 'alert' | 'recover'; code?: HealthCode; message: string }} HealthEvent */

/** @type {Set<(evt: HealthEvent) => void>} */
const listeners = new Set();

/** @type {HealthCode | null} */
let activeAlert = null;
/** @type {number} */
let lastAlertAt = 0;
/** @type {number} */
let inferSlowStreak = 0;
/** @type {number} */
let inferFailStreak = 0;
/** @type {number} */
let videoWatchTimer = 0;
/** @type {number} */
let lastVideoTime = -1;
/** @type {number} */
let lastVideoProgressAt = 0;

/** @returns {NonNullable<typeof devConfig.liveHealth>} */
function cfg() {
  return devConfig.liveHealth ?? {};
}

/**
 * @param {HealthEvent} evt
 */
function emit(evt) {
  for (const fn of listeners) fn(evt);
}

/**
 * @param {HealthCode} code
 */
function messageFor(code) {
  const map = uiLexicon.health;
  return map[code] ?? map.generic;
}

/**
 * @param {HealthCode} code
 */
function raiseAlert(code) {
  if (cfg().enabled === false) return;
  const now = performance.now();
  const cooldown = cfg().alertCooldownMs ?? 28000;
  if (activeAlert === code && now - lastAlertAt < cooldown) return;
  if (activeAlert && activeAlert !== code && now - lastAlertAt < cooldown) {
    return;
  }
  activeAlert = code;
  lastAlertAt = now;
  emit({ kind: "alert", code, message: messageFor(code) });
}

/**
 * @param {HealthCode} code
 */
function recoverIf(code) {
  if (activeAlert !== code) return;
  activeAlert = null;
  if (code === "inferSlow") inferSlowStreak = 0;
  if (code === "inferFail") inferFailStreak = 0;
  emit({ kind: "recover", message: "" });
}

/**
 * @param {(evt: HealthEvent) => void} listener
 * @returns {() => void}
 */
export function subscribeLiveHealth(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resetLiveHealth() {
  activeAlert = null;
  lastAlertAt = 0;
  inferSlowStreak = 0;
  inferFailStreak = 0;
  stopVideoStallWatch();
}

/**
 * @param {string} _module
 * @param {number} durationMs
 * @param {number} intervalMs
 */
export function noteInferenceDuration(_module, durationMs, intervalMs) {
  if (cfg().enabled === false) return;
  const ratio = cfg().inferSlowRatio ?? 2.2;
  const budget = intervalMs * ratio;
  const need = cfg().inferSlowStreak ?? 3;

  if (durationMs > budget) {
    inferSlowStreak++;
    if (inferSlowStreak >= need) raiseAlert("inferSlow");
    return;
  }

  if (activeAlert === "inferSlow") {
    inferSlowStreak = Math.max(0, inferSlowStreak - 1);
    if (inferSlowStreak === 0) recoverIf("inferSlow");
  } else {
    inferSlowStreak = 0;
  }
}

export function noteInferenceFailure() {
  if (cfg().enabled === false) return;
  const need = cfg().inferErrorStreak ?? 2;
  inferFailStreak++;
  if (inferFailStreak >= need) raiseAlert("inferFail");
}

export function noteInferenceSuccess() {
  if (cfg().enabled === false) return;
  if (inferFailStreak > 0) inferFailStreak--;
  if (activeAlert === "inferFail" && inferFailStreak === 0) {
    recoverIf("inferFail");
  }
}

/**
 * @param {HTMLVideoElement | null | undefined} video
 */
export function startVideoStallWatch(video) {
  stopVideoStallWatch();
  if (cfg().enabled === false || !video) return;

  lastVideoTime = video.currentTime;
  lastVideoProgressAt = performance.now();
  const tickMs = cfg().videoCheckMs ?? 900;

  videoWatchTimer = window.setInterval(() => {
    if (!video.srcObject || video.paused || video.readyState < 2) return;

    const t = video.currentTime;
    const now = performance.now();
    if (t !== lastVideoTime) {
      lastVideoTime = t;
      lastVideoProgressAt = now;
      if (activeAlert === "videoStall") recoverIf("videoStall");
      return;
    }

    const stallMs = cfg().videoStallMs ?? 2200;
    if (now - lastVideoProgressAt >= stallMs) {
      raiseAlert("videoStall");
    }
  }, tickMs);
}

export function stopVideoStallWatch() {
  if (!videoWatchTimer) return;
  clearInterval(videoWatchTimer);
  videoWatchTimer = 0;
}

/** @returns {HealthCode | null} */
export function getActiveHealthAlert() {
  return activeAlert;
}
